import {Avatar} from 'antd'
import {getId} from 'apis/model/base'
import getTitle from 'helpers/getTitle'
import getLabelOfGender from 'helpers/getLabelOfGender'
import React, {useContext} from 'react'
import {Link} from 'react-router-dom'
import {FaUserGraduate, FaUserTie} from 'react-icons/fa6'
import useTranslate from 'modules/local/useTranslate'
import EntityList from '../../../EntityList'
import {bindQueryParam} from '../../../functions/routerHelper'
import {course_getCourseClassStudents_Api, timetable_getTeacherSubjectsOfCourseClass_Api} from '../../../../../apis'
import {NullAsDefault} from '../../../../Shared'
import getAvatar from '../../../../../helpers/getAvatar'
import getLinkToDetail from '../../../../../helpers/getLinkToDetail'
import getAliasName from '../../../../../helpers/getAliasName'
import {getGroupColor, getNameInitial} from '../../../../Workspace/Course/functions/renderCourseClassStudent'
import CourseClassContext from '../../CourseClassContext'
import EmptyHolder from '../../../../../components/EmptyHolder'
import {LoginContext} from 'components/LoginContext'
import classNames from "classnames";

// Tab GỘP "Giảng viên" + "Học viên" (trước là "Giảng viên" + "Môn học" - đã bỏ khối Môn học theo yêu
// cầu: chỉ giảng viên đang dạy lớp là đủ, môn học của giảng viên đó đã hiện ngay dưới tên rồi -> khối
// Môn học riêng dư thừa). Khối Học viên thay vào chỗ đó - đây là "danh sách CHỈ ĐỂ XEM" (dành cho
// chính học viên trong lớp xem bạn học, không phải tab quản trị "Học viên" đầy đủ tính năng - tab đó
// vẫn ở path riêng 'students', ẩn với học viên thường qua isInvisible ở courseClassRouteEntities.js)
// nên CHỈ hiện field an toàn/không nhạy cảm: tên (+ tên thánh), mã học viên, tổ/nhóm, giới tính -
// KHÔNG có SĐT/email/giấy tờ tuỳ thân như bảng quản trị đầy đủ (CourseClassStudentTab.js). Layout
// StudentRow mirror Y HỆT TeacherRow (đọc comment TeacherRow) cho đồng bộ 2 khối.
// Dữ liệu 100% thật: teacher list qua đúng API/EntityList đang dùng ở CourseClassTeachers.js, student
// list qua ĐÚNG API course_getCourseClassStudents_Api đang dùng ở CourseClassStudentTab.js (tab quản
// trị đầy đủ) - chỉ đổi UI hiển thị rút gọn, không đổi/thêm nguồn dữ liệu mới.

const TeacherRow = ({item}) => {
    const {teacher, subject} = item ?? {}
    const {avatar, prefix_title, fullname, user} = teacher ?? {}
    const color = getGroupColor(getTitle(subject) || fullname)
    const initial = getNameInitial(fullname)

    return (
        <div
            className={classNames(
                "px-3 py-2.5 flex items-center gap-3 background-50 rounded-xl transition-all duration-300",
                user ? 'border border-transparent hover:border-color-200 hover:scale-101 cursor-pointer' : ''
            )}>
            <Avatar
                size={44}
                src={avatar || getAvatar(user)}
                icon={<FaUserTie size={20} className="text-white"/>}
                style={{backgroundColor: color, flexShrink: 0}}
                className="flex flex-center"
            />
            <div className="flex flex-col min-w-0">
                {user ? (
                    <Link to={getLinkToDetail(user)} className="font-bold text-sm text-color-000 no-underline leading-snug truncate">
                        {prefix_title && <span className="mr-1 font-medium text-color-300">{prefix_title}</span>}
                        {fullname}
                    </Link>
                ) : (
                    <span className="font-bold text-sm text-color-000 leading-snug truncate">
                        {prefix_title && <span className="mr-1 font-medium text-color-300">{prefix_title}</span>}
                        {fullname}
                    </span>
                )}
                {subject?.title && (
                    <span className="font-medium text-xs text-gray-600 tracking-wider truncate">
                        {getTitle(subject)}
                    </span>
                )}
            </div>
        </div>
    )
}

const TeachersSection = () => {
    const t = useTranslate()
    const {item} = useContext(CourseClassContext)
    const course_class_id = getId(item)
    const apiInfo = timetable_getTeacherSubjectsOfCourseClass_Api

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col">
                <span className="font-semibold text-base text-gray-700 tracking-wide">
                    {t('teachers of classes')}
                </span>
                <div className="w-20 mt-1 h-1 rounded-full bg-gray-400"/>
            </div>
            <EntityList
                apiInfo={apiInfo}
                RenderEntity={React.Fragment}
                query={bindQueryParam({id: course_class_id})}
                renderItem={(entity, index) => <TeacherRow key={index} item={entity}/>}
                renderWrap={(children) => (
                    <div className="flex flex-col gap-2">
                        {children}
                    </div>
                )}
                renderEmpty={() => <EmptyHolder style={{border: 'none'}}/>}
            />
        </div>
    )
}

// Row học viên - mirror Y HỆT TeacherRow ở trên (bạn yêu cầu "làm layout giống giáo viên"): tên ở
// dòng trên (đậm, tên thánh làm prefix qua getAliasName - ĐÚNG cách patron_saint_name đã hiển thị ở
// mọi nơi khác trong app, xem renderCourseClassStudent.js), dòng dưới (subtitle) nối 3 field còn lại
// bằng dấu chấm giữa - mã học viên (font-mono xanh, giống mã môn học ở SubjectRow cũ) - tổ/nhóm -
// giới tính (getLabelOfGender() dùng chung, xem renderStudent.js). Avatar tô theo getGroupColor(tổ/
// nhóm) - CÙNG bảng màu với TeacherRow, khác key (tô theo tổ chứ không theo môn vì học viên không có
// khái niệm "môn dạy" như giảng viên).
const StudentRow = ({item, canSeeAll = false, myUserId}) => {
    const t = useTranslate()
    const {student, sub_class_name, role_code, attendance_summary} = item ?? {}
    const {avatar, user, fullname, student_idcode, gender} = student ?? {}
    const color = getGroupColor(sub_class_name || fullname)
    const genderLabel = (gender === 1 || gender === 2) ? t(getLabelOfGender(gender)) : null
    // % chuyên cần TỔNG HỢP toàn lớp (gộp mọi môn) do BE trả trong attendance_summary - null khi
    // lớp chưa có buổi điểm danh nào -> ẩn hẳn khối bên phải. Nhãn "student_leader" mirror ĐÚNG
    // cách tab quản trị (CourseClassStudentTab.js) đang render, chỉ có value student_leader.
    const attendancePct = attendance_summary?.percentage
    // Học viên thường KHÔNG xem % chuyên cần của bạn học khác - chỉ hiện khi người xem xem được cả
    // lớp (canSeeAll: admin/giảng viên/phụ trách/lớp trưởng) HOẶC đây là dòng của chính mình. Đây
    // là ẩn ở GIAO DIỆN - BE vẫn trả attendance_summary cho mọi dòng.
    const canSeePct = canSeeAll || (!!myUserId && getId(user) === myUserId)

    return (
        <div className="px-3 py-2.5 flex items-center gap-3 background-50 rounded-xl">
            {/* Ưu tiên avatar của chính student, không có thì lấy avatar của user gắn trực tiếp
                (student.user - CÙNG cách TeacherRow ở trên đang làm src={avatar || getAvatar(user)}),
                chỉ rơi về icon màu khi cả 2 đều không có (bạn báo avatar thật đang không hiện). */}
            <Avatar
                size={44}
                src={avatar || getAvatar(user)}
                icon={<FaUserGraduate size={20} className="text-white"/>}
                style={{backgroundColor: color, flexShrink: 0}}
                className="flex flex-center"
            />
            <div className="flex flex-col min-w-0 flex-1">
                <span className="font-bold text-sm text-color-000 leading-snug truncate">
                    {student?.patron_saint_name && (
                        <span className="mr-1 font-medium text-color-300">{getAliasName(student, 'patron_saint')}</span>
                    )}
                    {fullname}
                </span>
                <div className="flex flex-wrap items-center gap-x-3">
                    <span className="font-medium text-xs text-gray-600 tracking-wider truncate">
                        {student_idcode && (
                            <span className="font-mono font-semibold text-blue-500">#{student_idcode}</span>
                        )}
                        {student_idcode && sub_class_name && <span className="mx-1.5 text-color-200">•</span>}
                        {sub_class_name}
                        {(student_idcode || sub_class_name) && genderLabel && <span className="mx-1.5 text-color-200">•</span>}
                        {genderLabel}
                    </span>
                    {role_code === 'student_leader' && (
                        <span className="px-2 py-0.5 w-fit bg-secondary-50 rounded-full font-medium text-2xs text-secondary-500 italic tracking-wider">
                        {t('student_leader')}
                    </span>
                    )}
                </div>
            </div>
            {attendancePct != null && canSeePct && (
                <div className="flex flex-col items-end flex-shrink-0 leading-none">
                    <span className="font-bold text-sm text-color-000">{attendancePct}%</span>
                    <span className="mt-0.5 text-2xs text-gray-500 tracking-wider">{t('cc_percent')}</span>
                </div>
            )}
        </div>
    )
}

const StudentsSection = () => {
    const t = useTranslate()
    const login = useContext(LoginContext)
    const {item} = useContext(CourseClassContext)
    const course_class_id = getId(item)
    const apiInfo = course_getCourseClassStudents_Api

    // Ai xem được % chuyên cần của MỌI học viên: admin/phụ trách lớp (item.edit), giảng viên dạy
    // lớp (available_actions.can_grade), lớp trưởng (is_class_leader). Học viên thường -> StudentRow
    // chỉ cho xem dòng của chính mình (so getId(login)).
    const canSeeAll = !!(item?.edit || item?.available_actions?.can_grade || item?.is_class_leader)
    const myUserId = getId(login)

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col">
                <span className="font-semibold text-base text-gray-700 tracking-wide">
                    {t('list of students')}
                </span>
                <div className="w-20 mt-1 h-1 rounded-full bg-gray-400"/>
            </div>
            <EntityList
                apiInfo={apiInfo}
                RenderEntity={React.Fragment}
                query={bindQueryParam({id: course_class_id})}
                values={{sort_by: 'name asc'}}
                renderItem={(entity, index) => (
                    <StudentRow key={index} item={entity} canSeeAll={canSeeAll} myUserId={myUserId}/>
                )}
                renderWrap={(children) => (
                    <div className="flex flex-col gap-2">
                        {children}
                    </div>
                )}
                renderEmpty={() => <EmptyHolder style={{border: 'none'}}/>}
            />
        </div>
    )
}

const CourseClassTeachersStudentsTab = () => {
    const {item, renderWarnings = NullAsDefault} = useContext(CourseClassContext)
    const warnings = Array.from(item?.warnings ?? [])

    if (warnings.length) {
        return (
            <div className="w-full flex flex-col gap-3">
                {renderWarnings(warnings)}
            </div>
        )
    }

    return (
        // Bọc chung trong 1 card trắng (border/shadow/bo góc) thay vì để 2 khối trôi nổi trực tiếp
        // trên nền trang - khớp style card đã dùng ở OrgRow/EducationProfileCard cùng module.
        <div className="rounded-2xl overflow-hidden border border-color-50 shadow-sm background">
            <div className="p-4 flex flex-col gap-2">
                <TeachersSection/>
            </div>
            <div className="border-t border-color-50"/>
            <div className="p-4 flex flex-col gap-2">
                <StudentsSection/>
            </div>
        </div>
    )
}

export default CourseClassTeachersStudentsTab
